import AppContext from 'App/AppContext';
import { IPDefaultButton } from 'baseComponents';
import WarningMessage from 'components/Notification/WarningMessage';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalStorageData, LocalStorageKeys, removeLocalStorageData } from 'utils/localstorage';

//
// Persistent banner shown while the user views a backup checkpoint (read-only preview session).
// Leaving the preview switches back to the org unit the preview was started from.
//
const CheckpointPreviewBar = () => {
  const { t } = useTranslation(['adminCompany']);
  const appContext = useContext(AppContext);

  if (!appContext.user?.login?.isCheckpointPreview) return null;

  const leavePreview = async () => {
    appContext.setError(undefined);

    const returnOrgUnitId = getLocalStorageData(undefined, LocalStorageKeys.BackupPreviewReturnOrgUnit);

    //never fall back to the clone itself: in a preview session the clone is part of login.tenants (the
    //back-end appends it so the client can resolve the tenant it logged into), so a missing localStorage
    //key would otherwise make this button a no-op with no other way out of the preview
    const cloneTenantId = appContext.user.login.tenantId;
    const returnTenant =
      appContext.user.login.tenants.find((tn) => tn.id === returnOrgUnitId && tn.id !== cloneTenantId) ??
      appContext.user.login.tenants.find((tn) => tn.id !== cloneTenantId);

    if (!returnTenant) {
      //no other tenant in the list to switch to: a full reload logs in through the normal tenant
      //resolution instead of leaving the button a silent no-op
      removeLocalStorageData(undefined, LocalStorageKeys.BackupPreviewReturnOrgUnit);
      window.location.assign('/');

      return;
    }

    try {
      await appContext.switchOrgUnit(returnTenant);
      //only forget the return org unit after a successful switch, so a failed attempt can be retried
      removeLocalStorageData(undefined, LocalStorageKeys.BackupPreviewReturnOrgUnit);
    } catch (err) {
      appContext.setError(err);
    }
  };

  return (
    <WarningMessage message={t('adminCompany:Backups.Preview.Banner')}>
      <IPDefaultButton
        text={t('adminCompany:Backups.Preview.Leave')}
        styles={{ root: { marginTop: 8 } }}
        onClick={() => leavePreview()}
      />
    </WarningMessage>
  );
};

export default CheckpointPreviewBar;
